import { ARHelper } from '../../../utils/ar-helper';
import {
	activateARCfn,
	type ARLaunchSheetHandle,
	exportGLBCfn,
	getImageRenderCfn,
	isARAvailableCfn,
	prewarmModelViewerCfn
} from './functions';
import type {
	ARExportOptions,
	ExportManagerConfig,
	ImageRenderOptions,
	ModelExportOptions
} from './types';
import { ARStatus, ARTracking } from '../../../utils/ar-status.enum';

/**
 * Callbacks the React UI registers so it can react to AR session
 * lifecycle and tracking events from the WebXR path.
 */
export type ARCallbacks = {
	onStatusChange?: (status: ARStatus) => void;
	onTrackingChange?: (tracking: ARTracking) => void;
};

/**
 * ExportManager handles exporting 3D scenes to various formats:
 * - Image screenshots (PNG, JPEG, WebP)
 * - GLB/GLTF model files
 * - AR: USDZ for iOS AR Quick Look, or a GLB shown in a `<model-viewer>`
 *   WebXR launch sheet on Android and other WebXR-capable browsers
 *
 * @example
 * ```typescript
 * const exportManager = new ExportManager({
 *   renderer,
 *   scene,
 *   camera,
 *   islandHolder,
 *   carpet: null,
 *   backgroundColor: new Color('#ffffff'),
 *   skyTextureKey: 'sky/overcast',
 *   environmentMapTexture: null
 * });
 *
 * // Get screenshot
 * const imageUrl = exportManager.getImageRender({ pixelRatio: 2 });
 *
 * // Export GLB
 * await exportManager.exportGLB({ filename: 'kitchen.glb' });
 *
 * // Activate AR
 * await exportManager.activateAR();
 * ```
 */
export class ExportManager {
	#config: ExportManagerConfig;
	#arHelper: ARHelper;
	#arCallbacks: ARCallbacks = {};
	#arLaunchSheet: ARLaunchSheetHandle | null = null;

	constructor (config: Partial<ExportManagerConfig> = {}) {
		this.#config = {
			renderer: null,
			scene: null,
			camera: null,
			islandHolder: null,
			carpet: null,
			backgroundColor: null,
			skyTextureKey: 'sky/overcast',
			environmentMapTexture: null,
			...config
		};
		this.#arHelper = ARHelper.instance;
	}

	/**
	 * Update configuration
	 */
	updateConfig (config: Partial<ExportManagerConfig>): void {
		this.#config = { ...this.#config, ...config };
	}

	/**
	 * Render the current scene to an image data URL
	 */
	getImageRender (options: ImageRenderOptions = {}): string | null {
		return getImageRenderCfn(this, options);
	}

	/**
	 * Get image render with callback (legacy compatibility)
	 */
	getImageRenderWithCallback (
		pixelRatio: number,
		callback: (imageData: string) => void
	): void {
		const dataUrl = this.getImageRender({ pixelRatio });
		if (dataUrl) {
			callback(dataUrl);
		}
	}

	/**
	 * Export the scene as a GLB/GLTF file
	 */
	exportGLB (options: ModelExportOptions = {}): Promise<ArrayBuffer | null> {
		return exportGLBCfn(this, options);
	}

	/**
	 * Activate AR: iOS AR Quick Look, or the `<model-viewer>` WebXR launch
	 * sheet everywhere else (Android Chrome / Samsung Internet, headsets).
	 */
	activateAR (options: ARExportOptions = {}): Promise<void> {
		return activateARCfn(this, options);
	}

	/**
	 * Check if AR is available
	 */
	isARAvailable (): Promise<boolean> {
		return isARAvailableCfn(this);
	}

	/**
	 * Check if iOS Quick Look is available
	 */
	isIOSQuickLookAvailable (): boolean {
		return this.#arHelper.isArQuickLookCandidate();
	}

	/**
	 * Warm the lazy `@google/model-viewer` chunk so the WebXR launch sheet
	 * opens faster on first use. No-op on iOS, which uses Quick Look instead.
	 */
	prewarmAR (): void {
		if (this.#arHelper.isArQuickLookCandidate()) return;
		prewarmModelViewerCfn().catch((error: unknown) => {
			console.warn('[AR] model-viewer prewarm failed:', error);
		});
	}

	/**
	 * Register UI callbacks for AR session status and tracking changes.
	 * The WebXR path surfaces model-viewer's `ar-status` / `ar-tracking`
	 * events through these callbacks.
	 */
	setARCallbacks (callbacks: ARCallbacks): void {
		this.#arCallbacks = callbacks;
	}

	/**
	 * Dismiss the WebXR launch sheet, ending any AR session it started.
	 */
	stopAR (): Promise<void> {
		this.#arLaunchSheet?.close();
		this.#arLaunchSheet = null;
		return Promise.resolve();
	}

	/**
	 * Dispose of resources
	 */
	dispose (): void {
		this.#arLaunchSheet?.close();
		this.#arLaunchSheet = null;
	}

	// ============ Internal Accessors (for CFN files) ============

	/**
	 * Access config
	 */
	get configAccess (): ExportManagerConfig {
		return this.#config;
	}

	/**
	 * Access AR helper
	 */
	get arHelper (): ARHelper {
		return this.#arHelper;
	}

	/**
	 * Access the registered AR callbacks
	 */
	get arCallbacksAccess (): ARCallbacks {
		return this.#arCallbacks;
	}

	/**
	 * The open WebXR launch sheet, if any
	 */
	get arLaunchSheetAccess (): ARLaunchSheetHandle | null {
		return this.#arLaunchSheet;
	}

	set arLaunchSheetAccess (handle: ARLaunchSheetHandle | null) {
		this.#arLaunchSheet = handle;
	}
}
